// F-148 §4.7 scan-to-add reducer (AC31). Pure: changes local draft state only; autosave persists
// it. Lookups come from the unchanged F045 `findVariantByBarcode` contract.

import type { BarcodeLookupResult } from '../types/barcode';
import {
  RECEIVING_LIMITS,
  type ReceivingDraftLineInput,
  type ReceivingScanOutcome,
} from '../types/stock-receiving';
import { applyScanIncrement } from './receiving-line-quantities';

export type ReceivingScanLookup = BarcodeLookupResult | { status: 'lookup_failed' };

export interface ReceivingScanState<L extends ReceivingDraftLineInput = ReceivingDraftLineInput> {
  lines: readonly L[];
  /** Last `RECEIVING_LIMITS.scanAppliedCaptureMemory` applied capture ids, oldest first. */
  appliedCaptureIds: readonly string[];
}

export interface ReceivingScanOptions<L extends ReceivingDraftLineInput> {
  /** Produces the `clientLineId` for a new line. */
  createClientLineId: () => string;
  /** Hydrates a new line into the caller's line shape (identity for plain inputs). */
  createLine: (input: ReceivingDraftLineInput) => L;
}

export interface ReceivingScanResult<L extends ReceivingDraftLineInput> {
  state: ReceivingScanState<L>;
  outcome: ReceivingScanOutcome;
}

function remember(ids: readonly string[], captureId: string): string[] {
  const next = [...ids, captureId];
  const overflow = next.length - RECEIVING_LIMITS.scanAppliedCaptureMemory;
  return overflow > 0 ? next.slice(overflow) : next;
}

function unresolvedLine(clientLineId: string, barcode: string): ReceivingDraftLineInput {
  return {
    clientLineId,
    source: 'scan',
    sourceRowNumber: null,
    identifiers: { barcode, variantRef: null, supplierCode: null, supplierItemName: null },
    variantId: null,
    matchMethod: null,
    unitKind: 'unit',
    packsExpected: null,
    unitsPerPack: null,
    unitsPerPackConfirmed: null,
    expectedUnits: null,
    acceptedUnits: null,
    damagedUnits: null,
    missingUnits: null,
    note: null,
    acceptedAuto: true,
  };
}

/**
 * Applies one scan capture. Every capture id is applied at most once (`ignored_repeat`).
 * Line numbers in outcomes are 1-based positions in `state.lines`.
 */
export function applyReceivingScan<L extends ReceivingDraftLineInput>(
  state: ReceivingScanState<L>,
  lookup: ReceivingScanLookup,
  captureId: string,
  options: ReceivingScanOptions<L>,
): ReceivingScanResult<L> {
  if (state.appliedCaptureIds.includes(captureId)) {
    return { state, outcome: { kind: 'ignored_repeat' } };
  }
  const applied = remember(state.appliedCaptureIds, captureId);
  const unchanged = (outcome: ReceivingScanOutcome): ReceivingScanResult<L> => ({
    state: { lines: state.lines, appliedCaptureIds: applied },
    outcome,
  });

  if (lookup.status === 'lookup_failed') return unchanged({ kind: 'lookup_failed' });
  if (lookup.status === 'invalid') return unchanged({ kind: 'invalid' });

  const lines = state.lines;
  const atLimit = lines.length >= RECEIVING_LIMITS.maxLines;

  if (lookup.status === 'known') {
    const variant = lookup.variant;
    const matches = lines
      .map((line, index) => ({ line, lineNo: index + 1 }))
      .filter(({ line }) => line.variantId === variant.variantId);

    if (matches.length >= 2) {
      return unchanged({ kind: 'duplicate_lines', lineNos: matches.map((m) => m.lineNo) });
    }
    const single = matches.length === 1 ? matches[0] : undefined;
    if (single) {
      const { line, lineNo } = single;
      if (line.unitKind === 'pack') return unchanged({ kind: 'pack_line', lineNo });
      const incremented = applyScanIncrement(line);
      if (incremented === null) return unchanged({ kind: 'quantity_limit', lineNo });
      const nextLines = lines.map((l, index) => (index === lineNo - 1 ? incremented : l));
      return {
        state: { lines: nextLines, appliedCaptureIds: applied },
        outcome: {
          kind: 'incremented',
          lineNo,
          productName: variant.productName,
          optionSummary: variant.variantOptionSummary,
          expectedUnits: incremented.expectedUnits ?? 0,
          acceptedUnits: incremented.acceptedUnits ?? 0,
        },
      };
    }
    if (atLimit) return unchanged({ kind: 'line_limit' });
    const input: ReceivingDraftLineInput = {
      clientLineId: options.createClientLineId(),
      source: 'scan',
      sourceRowNumber: null,
      identifiers: {
        barcode: lookup.normalized,
        variantRef: null,
        supplierCode: null,
        supplierItemName: null,
      },
      variantId: variant.variantId,
      matchMethod: 'barcode',
      unitKind: 'unit',
      packsExpected: null,
      unitsPerPack: null,
      unitsPerPackConfirmed: null,
      expectedUnits: 1,
      acceptedUnits: 1,
      damagedUnits: 0,
      missingUnits: 0,
      note: null,
      acceptedAuto: true,
    };
    const nextLines = [...lines, options.createLine(input)];
    return {
      state: { lines: nextLines, appliedCaptureIds: applied },
      outcome: {
        kind: 'added',
        lineNo: nextLines.length,
        productName: variant.productName,
        optionSummary: variant.variantOptionSummary,
        expectedUnits: 1,
      },
    };
  }

  // 'unknown' | 'deleted_recoverable'
  const normalized = lookup.normalized;
  const existingIndex = lines.findIndex(
    (line) => line.variantId === null && line.identifiers.barcode === normalized,
  );
  if (existingIndex >= 0)
    return unchanged({ kind: 'already_unresolved', lineNo: existingIndex + 1 });
  if (atLimit) return unchanged({ kind: 'line_limit' });
  const nextLines = [
    ...lines,
    options.createLine(unresolvedLine(options.createClientLineId(), normalized)),
  ];
  return {
    state: { lines: nextLines, appliedCaptureIds: applied },
    outcome: {
      kind: 'unresolved_added',
      lineNo: nextLines.length,
      reason: lookup.status === 'deleted_recoverable' ? 'ineligible' : 'unknown',
    },
  };
}

/**
 * §4.7 defensive camera dedup: a camera-sourced value identical to the previous camera value
 * within `scanCameraRepeatWindowMs` is dropped. Keyboard/wedge entries never use this.
 */
export function shouldIgnoreCameraRepeat(
  previous: { value: string; at: number } | null,
  next: { value: string; at: number },
  windowMs: number = RECEIVING_LIMITS.scanCameraRepeatWindowMs,
): boolean {
  if (previous === null) return false;
  return (
    previous.value === next.value && next.at - previous.at >= 0 && next.at - previous.at < windowMs
  );
}
